/**
 * DuplicateFinder — the same logo, five times, under five names.
 *
 * Sameness is decided on the contents, which means every file has to be read
 * once; that happens a batch at a time with the progress on show, because a
 * library of any size cannot be read inside one request.
 *
 * "Point them all at one copy" keeps every address working — the other
 * addresses simply resolve to the copy you kept, so no page needs re-linking
 * and nothing is deleted.
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Copy, Check, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { prettySize } from '@/lib/mediaLibrary';
import { AssetThumb } from '@/components/media/AssetThumb';

export const DuplicateFinder = ({ open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [keepers, setKeepers] = useState({});
  const [merging, setMerging] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['media-duplicates'],
    queryFn: () => api.get('/media/library/duplicates').then((r) => r.data),
    enabled: open,
  });

  const scan = async () => {
    setScanning(true);
    setScanned(0);
    try {
      // Keep asking until nothing is left unread. The cap is a safety net, not
      // an expectation — each round reads 60 files.
      for (let round = 0; round < 60; round += 1) {
        // eslint-disable-next-line no-await-in-loop
        const { data: step } = await api.post('/media/library/fingerprint', null,
          { params: { limit: 60 } });
        setScanned((n) => n + step.hashed);
        setRemaining(step.remaining);
        if (!step.remaining) break;
      }
      await refetch();
    } catch (e) {
      toast.error('The check could not finish — try again');
    } finally {
      setScanning(false);
    }
  };

  const merge = async (group) => {
    const keepId = keepers[group.digest] || group.copies[0].id;
    setMerging(group.digest);
    try {
      const { data: out } = await api.post('/media/library/duplicates/merge',
        { digest: group.digest, keep_id: keepId });
      toast.success(`Now sharing one copy — ${prettySize(out.freed_bytes)} freed`);
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media-library-facets'] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Those copies could not be merged');
    } finally {
      setMerging('');
    }
  };

  const groups = data?.groups || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="duplicate-finder">
        <DialogHeader>
          <DialogTitle>Duplicates</DialogTitle>
          <DialogDescription>
            Files whose contents are identical, however they were named. Keeping
            one and pointing the rest at it leaves every page working — the
            other addresses resolve to the copy you keep.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={scan} disabled={scanning} data-testid="duplicate-scan">
            {scanning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <Search className="mr-2 h-4 w-4" />}
            {scanning ? `Checking… ${scanned} read` : 'Check for duplicates'}
          </Button>
          {data ? (
            <p className="text-sm text-slate-600" data-testid="duplicate-summary">
              <strong>{data.duplicate_files}</strong> extra copies
              {data.wasted_bytes ? ` · ${prettySize(data.wasted_bytes)} of repeats` : ''}
            </p>
          ) : null}
          {remaining === 0 && !scanning && (
            <Badge variant="outline" className="text-emerald-700">
              <Check className="mr-1 h-3 w-3" />everything checked
            </Badge>
          )}
        </div>

        <div className="max-h-[52vh] space-y-3 overflow-y-auto">
          {isLoading ? (
            <p className="py-10 text-center text-sm text-slate-500">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Looking…
            </p>
          ) : groups.length === 0 ? (
            <div className="py-10 text-center" data-testid="duplicate-none">
              <Sparkles className="mx-auto mb-2 h-6 w-6 text-emerald-500" />
              <p className="text-sm font-medium text-slate-700">No duplicates found</p>
              <p className="mt-1 text-xs text-slate-500">
                Run the check above if files have been uploaded since the last one.
              </p>
            </div>
          ) : groups.map((g) => {
            const keep = keepers[g.digest] || g.copies[0].id;
            return (
              <div key={g.digest} className="rounded-xl border border-slate-200 p-3"
                   data-testid={`duplicate-group-${g.digest.slice(0, 10)}`}>
                <div className="flex items-start gap-3">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-slate-50">
                    <AssetThumb asset={g.copies[0]} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">
                      {g.count} copies of the same file
                    </p>
                    <p className="text-xs text-slate-500">
                      {prettySize(g.size)} each · {prettySize(g.extra_bytes)} repeated
                    </p>
                  </div>
                  <Button size="sm" disabled={merging === g.digest}
                          onClick={() => merge(g)}
                          data-testid={`duplicate-merge-${g.digest.slice(0, 10)}`}>
                    {merging === g.digest
                      ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      : <Copy className="mr-2 h-3.5 w-3.5" />}
                    Point them at one
                  </Button>
                </div>

                <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-2">
                  {g.copies.map((c, i) => (
                    <label key={c.id}
                           className="flex cursor-pointer items-center gap-2 text-xs text-slate-600"
                           data-testid={`duplicate-copy-${c.id}`}>
                      <input type="radio" name={`keep-${g.digest}`} checked={keep === c.id}
                             onChange={() => setKeepers((k) => ({ ...k, [g.digest]: c.id }))} />
                      <span className="truncate font-medium text-slate-800">{c.name}</span>
                      <span className="text-slate-400">{c.purpose}</span>
                      <span className="ml-auto shrink-0 text-slate-400">
                        {c.uploaded_at ? c.uploaded_at.slice(0, 10) : ''}
                        {i === 0 ? ' · oldest' : ''}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateFinder;
